import { ACCESS_TOKEN_KEY, REFRESH_TOKEN_KEY } from '@/modules/sign/constants';
import { useSignStore } from '@/modules/sign/store';
import { useAppStore } from '@/store';
import axios, { type AxiosInstance, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
const URL = import.meta.env.VITE_API_URL;
import router from '@/router';

export interface ApiResponse<T> {
  status: number;
  message: string;
  code: number;
  data: T;
  dataTime: string;
  httpStatus: string;
}

type ApiResponsePromise<T> = Promise<AxiosResponse<ApiResponse<T>>>;

const httpClient: AxiosInstance = axios.create({
  ...(URL && { baseURL: URL }),
  withCredentials: false,
  headers: {
    Accept: 'application/json',
    'content-type': 'application/json',
  },
});
let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (err: any) => void }[] = [];

function processQueue(error: any, token: string | null = null) {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });
  failedQueue = [];
}

const errorHandler = () => {
  // store의 토큰 상태도 초기화
  useSignStore().token = '';
  Cookies.remove(ACCESS_TOKEN_KEY, { path: '/' });
  Cookies.remove(REFRESH_TOKEN_KEY, { path: '/' });

  // 현재 경로가 로그인 페이지가 아닐 때만 이동
  if (router.currentRoute.value.path !== '/sign') {
    router.push('/sign');
  }
};
// 요청 인터셉터 (토큰 같은 거 추가할 때 사용)
httpClient.interceptors.request.use(
  (config) => {
    // refresh-token 요청일 때는 인터셉터에서 토큰을 추가하지 않음 (config에서 직접 전달)
    // refresh-token 요청은 백그라운드에서 조용히 처리되므로 로딩 표시하지 않음
    if (config.url?.includes('/user/refresh-token')) {
      return config;
    }

    // 요청시마다 쿠키에서 최신 토큰을 가져와서 헤더에 추가
    const token = Cookies.get(ACCESS_TOKEN_KEY);
    if (token) {
      config.headers['Authorization'] = 'Bearer ' + token;
    }

    useAppStore().startLoading();

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// 응답 인터셉터 (에러 처리 공통화)
httpClient.interceptors.response.use(
  (response) => {
    useAppStore().stopLoading();
    return response;
  },
  async (error) => {
    useAppStore().stopLoading();
    const originalRequest = error.config;

    // refreshToken 요청 자체가 실패한 경우는 재시도하지 않음
    if (error.config?.url?.includes('/user/refresh-token')) {
      isRefreshing = false; // 리프레시 토큰 요청 실패 시 플래그 리셋
      processQueue(error, null); // 대기 중인 요청들 모두 실패 처리
      errorHandler();
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      // 401 에러는 재시도할 예정이므로 여기서는 stopLoading을 호출하지 않음
      // 재시도 요청이 완료되면 그때 응답 인터셉터에서 처리됨

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = 'Bearer ' + token;
            return httpClient(originalRequest);
          })
          .catch((err) => {
            // 재시도 실패 시에만 로딩 해제
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = Cookies.get(REFRESH_TOKEN_KEY);
        if (!refreshToken) {
          isRefreshing = false;
          errorHandler();
          return Promise.reject(error);
        }

        // 리프레시 토큰 요청을 위한 별도 헤더 설정
        const refreshRequestConfig = {
          headers: {
            Authorization: 'Bearer ' + refreshToken,
          },
        };
        await useSignStore().refreshToken(refreshRequestConfig);

        // refreshToken 함수 내부에서 이미 setAccessToken과 setRefreshToken을 호출했으므로 쿠키에서 가져옴
        const newAccessToken = Cookies.get(ACCESS_TOKEN_KEY);
        if (!newAccessToken) {
          isRefreshing = false;
          errorHandler();
          return Promise.reject(new Error('Failed to get new access token'));
        }

        httpClient.defaults.headers.common['Authorization'] = 'Bearer ' + newAccessToken;

        isRefreshing = false; // 성공 시 플래그 리셋
        processQueue(null, newAccessToken);
        originalRequest.headers['Authorization'] = 'Bearer ' + newAccessToken;
        // 재시도 요청은 새로운 요청이므로 자동으로 startLoading이 호출됨
        return httpClient(originalRequest);
      } catch (err) {
        isRefreshing = false; // 먼저 isRefreshing을 false로 설정
        processQueue(err, null);
        errorHandler();
        return Promise.reject(err);
      }
    }

    return Promise.reject(error);
  },
);

const apiClient = {
  ...httpClient,
  get<T>(url: string, config?: AxiosRequestConfig): ApiResponsePromise<T> {
    return httpClient.get<ApiResponse<T>>(url, config);
  },
  post<T>(url: string, data?: any, config?: AxiosRequestConfig): ApiResponsePromise<T> {
    return httpClient.post<ApiResponse<T>>(url, data, config);
  },
  put<T>(url: string, data?: any, config?: AxiosRequestConfig): ApiResponsePromise<T> {
    return httpClient.put<ApiResponse<T>>(url, data, config);
  },
  delete<T>(url: string, config?: AxiosRequestConfig): ApiResponsePromise<T> {
    return httpClient.delete<ApiResponse<T>>(url, config);
  },
  file<T>(url: string, data?: any, config?: AxiosRequestConfig): ApiResponsePromise<T> {
    return httpClient.post<ApiResponse<T>>(url, data, {
      ...(config?.headers || {}),
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  /**
   * HTML 응답을 받는 POST 요청
   * @param url - API 엔드포인트
   * @param data - 요청 데이터
   * @param config - 추가 설정
   * @returns HTML 문자열을 반환하는 Promise
   */
  postHtml(url: string, data?: any, config?: AxiosRequestConfig): Promise<string> {
    return httpClient
      .post<string>(url, data, {
        ...config,
        responseType: 'text',
        headers: {
          ...config?.headers,
          Accept: 'text/html',
        },
      })
      .then((response) => response.data);
  },
};

export default apiClient;
